import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import "./DocFinder.css";
import { FaArrowRight, FaTimes } from "react-icons/fa";

import { searchDocuments } from "../../api/docfinder";
import { getErrorMessage } from "../../utils/errorHandling";

const DocumentFinder = () => {
    const navigate = useNavigate();
    const [query, setQuery] = useState("");
    const [submittedQuery, setSubmittedQuery] = useState("");
    const [showResultsView, setShowResultsView] = useState(false);

    // API States
    const [results, setResults] = useState<any[]>([]);
    const [filteredResults, setFilteredResults] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const { getAccessTokenSilently } = useAuth0();

    // --- Search & API Effect ---
    const fetchResults = async (searchQuery: string) => {
        if (!searchQuery.trim()) {
            setResults([]);
            return;
        }
        setIsLoading(true);
        setError(null);
        try {
            const token = await getAccessTokenSilently({
                authorizationParams: {
                    audience: "auth-service"
                }
            });
            const data: any = await searchDocuments(searchQuery, token);
            if (data?.results && Array.isArray(data.results)) {
                const formatDate = (dateStr: string) => {
                    if (!dateStr) return "-";
                    const date = new Date(dateStr);
                    return isNaN(date.getTime()) ? "-" : date.toLocaleDateString("en-GB");
                };

                const formatValue = (val: any) => {
                    if (val === undefined || val === null || val === "") return "-";
                    if (typeof val === 'object') return JSON.stringify(val);
                    return String(val);
                };

                setResults(data.results.map((item: any) => ({
                    documentDate: formatDate(item.created_date),
                    fileName: formatValue(item.title),
                    owner: formatValue(item.owner),
                    projectId: formatValue(item.metadata?.dossier_id),
                    folder: formatValue(item.metadata?.chapter_id),
                    version: formatValue(item.version),
                    lastModified: formatDate(item.last_modified_date),
                    fileType: formatValue(item.file_type),
                    location: "sharepoint",
                    locatedAt: formatValue(item.metadata?.located_at),
                    originalFileName: item.file_name || item.metadata?.file_name || item.name || item.title || "",
                    access: String(item.access ?? true),
                    downloadUrl: item.downloadable_url || "#",
                })));
            } else {
                setResults([]);
            }
        } catch (err: unknown) {
            setError(`Failed to fetch documents: ${getErrorMessage(err)}`);
            setResults([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSearch = () => {
        if (query.trim()) {
            setSubmittedQuery(query);
            setShowResultsView(true);
            void fetchResults(query);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSearch();
        }
    };
    const [semanticScore, setSemanticScore] = useState(50);
    const [activeFilters, setActiveFilters] = useState({
        projectId: [] as string[],
        access: [] as string[],
        owner: [] as string[],
        location: [] as string[],
        fileType: [] as string[],
    });
    const [openDropdown, setOpenDropdown] = useState<string | null>(null);

    // --- Filtering Logic ---
    useEffect(() => {
        let data = [...results];

        if (activeFilters.projectId.length > 0) {
            data = data.filter(item => activeFilters.projectId.includes(item.projectId));
        }
        if (activeFilters.access.length > 0) {
            data = data.filter(item => activeFilters.access.includes(String(item.access)));
        }
        if (activeFilters.owner.length > 0) {
            data = data.filter(item => activeFilters.owner.includes(item.owner));
        }
        if (activeFilters.location.length > 0) {
            data = data.filter(item => activeFilters.location.includes(item.location));
        }
        if (activeFilters.fileType.length > 0) {
            data = data.filter(item => activeFilters.fileType.includes(item.fileType));
        }

        setFilteredResults(data);
    }, [results, activeFilters]);

    const [documentToChat, setDocumentToChat] = useState<string | null>(null);

    // --- Filter Helpers ---

    // Extract unique values for a specific field from the actual results API data
    const getUniqueValues = (field: string) => {
        const values = results.map((item) => String(item[field] || ""));
        return Array.from(new Set(values)).filter(v => v !== "undefined" && v !== "");
    };

    const toggleDropdown = (dropdownName: string) => {
        setOpenDropdown(openDropdown === dropdownName ? null : dropdownName);
    };

    const toggleFilter = (filterCategory: keyof typeof activeFilters, value: string) => {
        setActiveFilters((prev) => {
            const currentSelected = prev[filterCategory];
            if (currentSelected.includes(value)) {
                // Remove it
                return { ...prev, [filterCategory]: currentSelected.filter((v) => v !== value) };
            } else {
                // Add it
                return { ...prev, [filterCategory]: [...currentSelected, value] };
            }
        });
    };

    const getFilterClass = (category: keyof typeof activeFilters) => {
        const isActive = activeFilters[category].length > 0;
        return `filter-chip ${isActive ? 'active' : ''}`;
    };

    // Close dropdowns if clicking outside (simplified for now by just attaching an onClick to the main container)
    const closeDropdowns = () => {
        if (openDropdown) setOpenDropdown(null);
    };

    const textareaRef = useRef<HTMLTextAreaElement>(null);

    // Resize textarea on input
    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = "auto";
            textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
        }
    }, [query]);


    const clearSearch = (e?: React.MouseEvent) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        setQuery("");
        setSubmittedQuery("");
        setResults([]);
    };



    const handleChatClick = (fileName: string) => {
        setDocumentToChat(fileName);
    };

    const confirmChat = () => {
        if (documentToChat) {
            navigate("/chat", { state: { selectedDocument: documentToChat } });
        }
    };

    const cancelChat = () => {
        setDocumentToChat(null);
    };

    if (showResultsView) {
        return (
            <div className="doc-finder-container results-mode" onClick={closeDropdowns}>
                <div className="results-header-container">
                    <div className={`search-input-wrapper outline-active`}>
                        <textarea
                            ref={textareaRef}
                            className="search-textarea"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Ask a question or search by keyword to find documents"
                            rows={1}
                        />
                        {query && (query === submittedQuery ? (
                            <button type="button" className="clear-search-button" onClick={clearSearch}>
                                <FaTimes />
                            </button>
                        ) : (
                            <button type="button" className="send-button active" onClick={handleSearch}>
                                <FaArrowRight />
                            </button>
                        ))}
                    </div>

                    <div className="semantic-scoring">
                        <input
                            type="range"
                            min="0"
                            max="100"
                            value={semanticScore}
                            onChange={(e) => setSemanticScore(Number(e.target.value))}
                            className="semantic-slider"
                            style={{
                                background: `linear-gradient(to right, #6566ff 0%, #6566ff ${semanticScore}%, #e2e8f0 ${semanticScore}%, #e2e8f0 100%)`
                            }}
                        />
                        <div className="slider-label">Semantic scoring</div>
                    </div>
                </div>

                <div className="results-meta">
                    <div className="result-count">{filteredResults.length} / {results.length} results</div>

                    <div className="filter-container">
                        {/* Project ID Dropdown */}
                        <div className="filter-dropdown-wrapper">
                            <div className={getFilterClass('projectId')} onClick={(e) => { e.stopPropagation(); toggleDropdown('projectId'); }}>
                                Project ID <span className="arrow-down">▼</span>
                                {activeFilters.projectId.length > 0 && <span className="badge">{activeFilters.projectId.length}</span>}
                            </div>
                            {openDropdown === 'projectId' && (
                                <div className="dropdown-menu" onClick={(e) => e.stopPropagation()}>
                                    {getUniqueValues('projectId').map((val) => (
                                        <label key={val} className="dropdown-item" title={val}>
                                            <input
                                                type="checkbox"
                                                className="custom-checkbox"
                                                checked={activeFilters.projectId.includes(val)}
                                                onChange={() => toggleFilter('projectId', val)}
                                            />
                                            <span className="truncate-text">{val}</span>
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Access Dropdown */}
                        <div className="filter-dropdown-wrapper">
                            <div className={getFilterClass('access')} onClick={(e) => { e.stopPropagation(); toggleDropdown('access'); }}>
                                Access <span className="arrow-down">▼</span>
                                {activeFilters.access.length > 0 && <span className="badge">{activeFilters.access.length}</span>}
                            </div>
                            {openDropdown === 'access' && (
                                <div className="dropdown-menu" onClick={(e) => e.stopPropagation()}>
                                    <label className="dropdown-item" title="Granted">
                                        <input
                                            type="checkbox"
                                            className="custom-checkbox"
                                            checked={activeFilters.access.includes('true')}
                                            onChange={() => toggleFilter('access', 'true')}
                                        />
                                        <span className="truncate-text">Granted</span>
                                    </label>
                                    <label className="dropdown-item" title="Denied">
                                        <input
                                            type="checkbox"
                                            className="custom-checkbox"
                                            checked={activeFilters.access.includes('false')}
                                            onChange={() => toggleFilter('access', 'false')}
                                        />
                                        <span className="truncate-text">Denied</span>
                                    </label>
                                </div>
                            )}
                        </div>

                        {/* Owner Dropdown */}
                        <div className="filter-dropdown-wrapper">
                            <div className={getFilterClass('owner')} onClick={(e) => { e.stopPropagation(); toggleDropdown('owner'); }}>
                                Owner <span className="arrow-down">▼</span>
                                {activeFilters.owner.length > 0 && <span className="badge">{activeFilters.owner.length}</span>}
                            </div>
                            {openDropdown === 'owner' && (
                                <div className="dropdown-menu" onClick={(e) => e.stopPropagation()}>
                                    {getUniqueValues('owner').map((val) => (
                                        <label key={val} className="dropdown-item" title={val}>
                                            <input
                                                type="checkbox"
                                                className="custom-checkbox"
                                                checked={activeFilters.owner.includes(val)}
                                                onChange={() => toggleFilter('owner', val)}
                                            />
                                            <span className="truncate-text">{val}</span>
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Location Dropdown */}
                        <div className="filter-dropdown-wrapper">
                            <div className={getFilterClass('location')} onClick={(e) => { e.stopPropagation(); toggleDropdown('location'); }}>
                                Location <span className="arrow-down">▼</span>
                                {activeFilters.location.length > 0 && <span className="badge">{activeFilters.location.length}</span>}
                            </div>
                            {openDropdown === 'location' && (
                                <div className="dropdown-menu" onClick={(e) => e.stopPropagation()}>
                                    {getUniqueValues('location').map((val) => (
                                        <label key={val} className="dropdown-item" title={val}>
                                            <input
                                                type="checkbox"
                                                className="custom-checkbox"
                                                checked={activeFilters.location.includes(val)}
                                                onChange={() => toggleFilter('location', val)}
                                            />
                                            <span className="truncate-text">{val}</span>
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Filetype Dropdown */}
                        <div className="filter-dropdown-wrapper">
                            <div className={getFilterClass('fileType')} onClick={(e) => { e.stopPropagation(); toggleDropdown('fileType'); }}>
                                Filetype <span className="arrow-down">▼</span>
                                {activeFilters.fileType.length > 0 && <span className="badge">{activeFilters.fileType.length}</span>}
                            </div>
                            {openDropdown === 'fileType' && (
                                <div className="dropdown-menu" onClick={(e) => e.stopPropagation()}>
                                    {getUniqueValues('fileType').map((val) => (
                                        <label key={val} className="dropdown-item" title={val}>
                                            <input
                                                type="checkbox"
                                                className="custom-checkbox"
                                                checked={activeFilters.fileType.includes(val)}
                                                onChange={() => toggleFilter('fileType', val)}
                                            />
                                            <span className="truncate-text">{val}</span>
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="results-table-container">
                    {isLoading ? (
                        <div style={{ padding: "40px", textAlign: "center", color: "#666", display: "flex", flexDirection: "column", alignItems: "center" }}>
                            <div className="loader-spinner" style={{ marginBottom: "15px", width: "30px", height: "30px", border: "4px solid #f3f3f3", borderTop: "4px solid #6566ff", borderRadius: "50%" }}></div>
                            <p style={{ margin: 0, fontWeight: "500" }}>Loading documents...</p>
                        </div>
                    ) : error ? (
                        <div style={{ padding: "40px", textAlign: "center", color: "#d32f2f" }}>
                            <p>{error}</p>
                            <button
                                onClick={() => fetchResults(submittedQuery)}
                                style={{ marginTop: "15px", padding: "8px 16px", cursor: "pointer", background: "#6566ff", color: "white", border: "none", borderRadius: "4px" }}
                            >
                                Retry
                            </button>
                        </div>
                    ) : filteredResults.length === 0 ? (
                        <div style={{ padding: "40px", textAlign: "center", color: "#666" }}>
                            <p>No results found for "{submittedQuery}" with selected filters.</p>
                        </div>
                    ) : (
                        <table className="results-table">
                            <thead>
                                <tr>
                                    <th style={{ width: '30%' }}>File name</th>
                                    <th>Actions</th>
                                    <th>Owner</th>
                                    <th>Project ID</th>
                                    <th style={{ width: '15%' }}>Folder ID</th>
                                    <th style={{ width: '8%', textAlign: 'center' }}>Version</th>
                                    <th>Last modified</th>
                                    <th style={{ width: '8%' }}>Filetype</th>
                                    <th>Location</th>
                                    <th style={{ width: '5%', textAlign: 'center' }}>Access</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredResults.map((result, index) => (
                                    <tr key={index}>
                                        <td className="file-name" title={result.fileName}>{result.fileName}</td>
                                        <td className="action-icons">
                                            <button className="action-btn" onClick={() => handleChatClick(result.originalFileName || result.fileName)} title="Zoom In / Chat">
                                                <img src="/svg/chatKG.svg" alt="Chat" style={{ width: '20px', height: '20px' }} />
                                            </button>
                                            <button
                                                className="action-btn"
                                                title="Open File"
                                                onClick={() => window.open(result.downloadUrl, '_blank')}
                                                style={{ opacity: result.downloadUrl === "#" ? 0.5 : 1, cursor: result.downloadUrl === "#" ? "not-allowed" : "pointer" }}
                                                disabled={result.downloadUrl === "#"}
                                            >
                                                <img src="/svg/expand.svg" alt="Expand" style={{ width: '20px', height: '20px' }} />
                                            </button>
                                        </td>
                                        <td>{result.owner}</td>
                                        <td className="project-id" title={result.projectId}>{result.projectId}</td>
                                        <td className="folder-id" title={result.folder}>{result.folder}</td>
                                        <td className="version-cell" title={result.version}>{result.version}</td>
                                        <td>{result.lastModified}</td>
                                        <td className="file-type" title={result.fileType}>{result.fileType}</td>
                                        <td title={result.locatedAt}>{result.location}</td>
                                        <td className="access-icon" style={{ textAlign: 'center' }}>
                                            {result.access === "true" ? (
                                                <img src="/svg/accessgranted.svg" alt="Granted" style={{ width: '20px', height: '20px' }} />
                                            ) : (
                                                <span className="access-denied" title="Denied">✕</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Chat Confirmation Modal Overlay */}
                {documentToChat && (
                    <div className="modal-overlay" onClick={cancelChat}>
                        <div className="chat-modal" onClick={e => e.stopPropagation()}>
                            <h3>Do you want to start chatting with this document?</h3>
                            <div className="modal-actions">
                                <button className="btn-cancel" onClick={cancelChat}>Cancel</button>
                                <button className="btn-confirm" onClick={confirmChat}>Confirm</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="doc-finder-container initial-mode">
            <div className="hero-section">
                <div className="hero-logo">
                    <img src="/chaticonlogo.png" alt="Loading..." className="loader-logo" />
                </div>
                <h1>Find documents the easy way</h1>
            </div>

            <div className="search-container">
                <div className={`search-input-wrapper ${query ? 'has-input' : ''}`}>
                    <textarea
                        ref={textareaRef}
                        className="search-textarea"
                        placeholder="Ask a question or search by keyword to find documents"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={handleKeyDown}
                        rows={1}
                    />
                    <button
                        className={`send-button ${query ? 'active' : ''}`}
                        onClick={handleSearch}
                        disabled={!query.trim()}
                    >
                        <FaArrowRight />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DocumentFinder;
